import { GRUPOS, LIMITES_ALERTA, type CodigoGrupo, gruposPresentes, nomeGrupo } from '@precificador/shared/pricing.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FileDown, FileSpreadsheet, Lock, LockOpen, RefreshCw, RotateCcw, Search, SlidersHorizontal, Trash2, Upload } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ErroApi } from '../api/cliente';
import { rodadas as apiRodadas } from '../api/endpoints';
import type { Divergencia, ItemRodada, ResultadoPublicacao, RodadaCompleta, StatusRodada } from '../api/tipos';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { CaixaMarcar, Campo, estiloEntrada } from '../components/Campo';
import { Dialogo } from '../components/Dialogo';
import { EntradaNumero } from '../components/EntradaNumero';
import { ComposicaoPrecoItem } from '../components/ComposicaoPreco';
import { FormParametros, type TextosParametros, lerTextos, paraTextos } from '../components/FormParametros';
import { SeloAmbiente } from '../components/Layout';
import { Carregando, Pagina } from '../components/Pagina';
import { useSessao } from '../hooks/useSessao';
import {
  formatarCusto,
  formatarDataHora,
  formatarPct,
  formatarPeso,
  formatarReais,
  formatarReaisKg,
  plural,
} from '../lib/formatos';

type Edicao = { margemPct?: number | null; negociacaoPct?: number | null; precoUnit?: number | null; liberarPreco?: boolean };

const ROTULO_STATUS: Record<StatusRodada, { texto: string; classe: string }> = {
  RASCUNHO: { texto: 'Rascunho', classe: 'bg-aviso-fundo text-aviso border-aviso-borda/60' },
  PUBLICADA: { texto: 'Publicada', classe: 'bg-ok-fundo text-ok border-ok/30' },
  DESCARTADA: { texto: 'Descartada', classe: 'bg-fundo text-tinta-2 border-linha' },
};

export function SeloStatus({ status }: { status: StatusRodada }) {
  const s = ROTULO_STATUS[status];
  return <span className={clsx('inline-block rounded-campo border px-2 py-0.5 text-[12px] font-semibold', s.classe)}>{s.texto}</span>;
}

function alertaVariacao(v: number | null): boolean {
  return v !== null && (v > LIMITES_ALERTA.variacaoMaxPct || v < LIMITES_ALERTA.variacaoMinPct);
}

function alertaMargem(m: number | null): boolean {
  return m !== null && m < LIMITES_ALERTA.margemDescontoMinPct;
}

function temAlerta(i: ItemRodada): boolean {
  return alertaVariacao(i.variacaoPct) || alertaMargem(i.margemDesconto) || (i.qtdLinhas > 1 && i.precoAtual !== i.precoAtualMin);
}

function editado(i: ItemRodada): boolean {
  return i.margemEditada || i.negociacaoEditada || i.precoEditado;
}

export function Previa() {
  const id = Number(useParams().id);
  const cliente = useQueryClient();
  const chave = ['rodadas', id];
  const { data, isPending, error } = useQuery({ queryKey: chave, queryFn: () => apiRodadas.obter(id), enabled: Number.isInteger(id) });
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro' | 'aviso'; texto: string } | null>(null);
  const [busca, setBusca] = useState('');
  const [soAlertas, setSoAlertas] = useState(false);
  const [soEditados, setSoEditados] = useState(false);
  const [soMantidos, setSoMantidos] = useState(false);
  const [dialogo, setDialogo] = useState<'parametros' | 'publicar' | 'descartar' | null>(null);
  const [detalhe, setDetalhe] = useState<ItemRodada | null>(null);
  const [baixando, setBaixando] = useState<'pdf' | 'xlsx' | null>(null);

  function atualizar(r: RodadaCompleta) {
    cliente.setQueryData(chave, { rodada: r.rodada, itens: r.itens, resumo: r.resumo });
    void cliente.invalidateQueries({ queryKey: ['rodadas'], exact: true });
  }

  const falhou = (padrao: string) => (e: unknown) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : padrao });

  const editar = useMutation({
    mutationFn: ({ itemId, edicao }: { itemId: number; edicao: Edicao }) => apiRodadas.editarItem(id, itemId, edicao),
    onSuccess: (r) => {
      atualizar(r);
      setMensagem(null);
    },
    onError: falhou('Não foi possível alterar o item.'),
  });
  const { mutateAsync: editarItem } = editar;
  const aoEditar = useCallback((itemId: number, edicao: Edicao) => editarItem({ itemId, edicao }), [editarItem]);

  const trava = useMutation({
    mutationFn: (travarPreco: boolean) => apiRodadas.definirTrava(id, travarPreco),
    onSuccess: (r) => {
      atualizar(r);
      setMensagem({
        tipo: 'ok',
        texto: r.rodada.travarPreco
          ? `Manter preços atuais ligado: ${plural(r.resumo.mantidos, 'item ficou com o preço mantido', 'itens ficaram com o preço mantido')}.`
          : 'Manter preços atuais desligado: os preços seguem o cálculo.',
      });
    },
    onError: falhou('Não foi possível alterar a trava.'),
  });

  const recarregar = useMutation({
    mutationFn: () => apiRodadas.recarregarCustos(id),
    onSuccess: (r) => {
      atualizar(r);
      setMensagem({
        tipo: 'ok',
        texto: r.custosAlterados ? `${plural(r.custosAlterados, 'custo atualizado', 'custos atualizados')} e recalculados.` : 'Os custos cadastrados são os mesmos da prévia.',
      });
    },
    onError: falhou('Não foi possível recarregar os custos.'),
  });

  async function baixar(tipo: 'pdf' | 'xlsx') {
    setBaixando(tipo);
    try {
      await (tipo === 'pdf' ? apiRodadas.baixarPdf(id) : apiRodadas.baixarXlsx(id));
    } catch (e) {
      falhou('Não foi possível gerar o arquivo.')(e);
    } finally {
      setBaixando(null);
    }
  }

  const itens = data?.itens ?? [];
  const aAtualizar = useMemo(() => itens.filter((i) => i.situacao === 'atualizar' || i.situacao === 'mantido' || i.situacao === 'erro'), [itens]);
  const naoAlterados = useMemo(() => itens.filter((i) => i.situacao === 'sem_custo' || i.situacao === 'sem_peso'), [itens]);
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return aAtualizar.filter(
      (i) =>
        (!soAlertas || temAlerta(i)) &&
        (!soEditados || editado(i)) &&
        (!soMantidos || i.situacao === 'mantido') &&
        (!termo || i.cdMaterial.toLowerCase().includes(termo) || i.descricao.toLowerCase().includes(termo)),
    );
  }, [aAtualizar, busca, soAlertas, soEditados, soMantidos]);

  if (isPending) return <Carregando />;
  if (error) {
    return (
      <Pagina titulo={`Rodada ${id}`}>
        <Aviso tipo="erro">{error.message}</Aviso>
      </Pagina>
    );
  }

  const { rodada, resumo } = data;
  const rascunho = rodada.status === 'RASCUNHO';
  const grupos = GRUPOS.map((g) => g.codigo).filter((g) => rodada.parametros[g]);

  return (
    <Pagina
      larga
      titulo={`Tabela ${rodada.tbPreco} — ${rodada.descricaoTabela}`}
      contexto={
        <span className="flex flex-wrap items-center gap-2">
          <SeloStatus status={rodada.status} />
          <span>
            Rodada {rodada.id} · criada por {rodada.criadoPor ?? '—'} em {formatarDataHora(rodada.criadoEm)}
            {rodada.publicadoEm && (
              <>
                {' '}
                · publicada por {rodada.publicadoPor ?? '—'} em {formatarDataHora(rodada.publicadoEm)}
                {rodada.publicadoEmBanco && <> ({rodada.publicadoEmBanco})</>}
              </>
            )}
          </span>
        </span>
      }
      acoes={
        <>
          {rascunho && (
            <>
              <Botao variante="secundario" tamanho="compacto" onClick={() => setDialogo('parametros')}>
                <SlidersHorizontal aria-hidden className="size-3.5" /> Parâmetros
              </Botao>
              <Botao variante="secundario" tamanho="compacto" carregando={recarregar.isPending} onClick={() => recarregar.mutate()}>
                <RefreshCw aria-hidden className="size-3.5" /> Recarregar custos
              </Botao>
            </>
          )}
          <Botao variante="secundario" tamanho="compacto" carregando={baixando === 'pdf'} onClick={() => void baixar('pdf')}>
            <FileDown aria-hidden className="size-3.5" /> {rodada.status === 'PUBLICADA' ? 'PDF' : 'PDF da prévia'}
          </Botao>
          <Botao variante="secundario" tamanho="compacto" carregando={baixando === 'xlsx'} onClick={() => void baixar('xlsx')}>
            <FileSpreadsheet aria-hidden className="size-3.5" /> Exportar XLSX
          </Botao>
          {rascunho && (
            <>
              <Botao variante="perigo" tamanho="compacto" onClick={() => setDialogo('descartar')}>
                <Trash2 aria-hidden className="size-3.5" /> Descartar
              </Botao>
              <Botao variante="principal" tamanho="compacto" disabled={resumo.atualizar === 0} onClick={() => setDialogo('publicar')}>
                <Upload aria-hidden className="size-3.5" /> Publicar no CIGAM
              </Botao>
            </>
          )}
        </>
      }
    >
      <Resumo data={data} />

      {rodada.ultimoErro && rascunho && (
        <Aviso tipo="erro" titulo="A última tentativa de publicação falhou" className="mb-3">
          {rodada.ultimoErro}
        </Aviso>
      )}
      {resumo.repetidos > 0 && (
        <Aviso tipo="aviso" className="mb-3">
          {plural(resumo.repetidos, 'material aparece', 'materiais aparecem')} em mais de uma linha (Regra) da tabela: todas as linhas recebem o
          mesmo preço.
          {resumo.repetidosComPrecoDiferente > 0 && (
            <strong> {plural(resumo.repetidosComPrecoDiferente, 'deles tem', 'deles têm')} preços diferentes hoje entre as linhas.</strong>
          )}
        </Aviso>
      )}
      {mensagem && (
        <Aviso tipo={mensagem.tipo} className="mb-3">
          {mensagem.texto}
        </Aviso>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-painel border border-linha bg-superficie px-3 py-2">
        <Lock aria-hidden className="size-4 text-tinta-2" />
        {rascunho ? (
          <CaixaMarcar
            rotulo="Manter preços atuais"
            checked={rodada.travarPreco}
            disabled={trava.isPending}
            onChange={(e) => trava.mutate(e.target.checked)}
          />
        ) : (
          <span className="font-medium">Manter preços atuais: {rodada.travarPreco ? 'ligado' : 'desligado'}</span>
        )}
        <span className="text-[13px] text-tinta-2">
          {rodada.travarPreco
            ? 'Nenhum preço muda, nem para mais nem para menos: só a margem é recalculada. Clique no cadeado do item para liberar o preço calculado, ou digite o preço.'
            : 'Os preços seguem o cálculo, para mais ou para menos.'}
        </span>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-4">
        <div className="relative w-72">
          <Search aria-hidden className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-tinta-2" />
          <input
            type="search"
            aria-label="Buscar item"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar código ou descrição"
            className={clsx(estiloEntrada, 'border-linha pl-8')}
          />
        </div>
        <CaixaMarcar rotulo="Somente com alerta" checked={soAlertas} onChange={(e) => setSoAlertas(e.target.checked)} />
        <CaixaMarcar rotulo="Somente editados" checked={soEditados} onChange={(e) => setSoEditados(e.target.checked)} />
        <CaixaMarcar rotulo="Somente preços mantidos" checked={soMantidos} onChange={(e) => setSoMantidos(e.target.checked)} />
        <Legenda />
      </div>

      <TabelaItens
        itens={visiveis}
        comAcrescimo={itens.some((i) => i.acrescimoUnit > 0)}
        travaAtiva={rodada.travarPreco}
        editavel={rascunho}
        aoEditar={aoEditar}
        ocupado={editar.isPending ? editar.variables?.itemId : undefined}
        aoDetalhar={setDetalhe}
      />

      {naoAlterados.length > 0 && <NaoAlterados itens={naoAlterados} />}

      <Dialogo aberto={dialogo === 'parametros'} titulo="Parâmetros da rodada" largura="max-w-4xl" aoFechar={() => setDialogo(null)}>
        {dialogo === 'parametros' && (
          <EditarParametros
            data={data}
            grupos={grupos}
            aoConcluir={(r) => {
              atualizar(r);
              setDialogo(null);
              setMensagem({
                tipo: r.preservados > 0 ? 'aviso' : 'ok',
                texto:
                  r.preservados > 0
                    ? `Grupo recalculado. ${plural(r.preservados, 'item editado manualmente foi preservado', 'itens editados manualmente foram preservados')} (margem, negociação ou preço).`
                    : 'Grupo recalculado com os novos parâmetros.',
              });
            }}
          />
        )}
      </Dialogo>

      <Dialogo aberto={dialogo === 'publicar'} titulo="Publicar no CIGAM" largura="max-w-xl" aoFechar={() => setDialogo(null)}>
        {dialogo === 'publicar' && <Publicar data={data} aoConcluir={atualizar} aoFechar={() => setDialogo(null)} />}
      </Dialogo>

      <Dialogo
        aberto={detalhe !== null}
        titulo="Composição do preço"
        descricao={detalhe ? `${detalhe.cdMaterial} — ${detalhe.descricao}` : undefined}
        largura="max-w-2xl"
        aoFechar={() => setDetalhe(null)}
      >
        {detalhe && rodada.parametros[detalhe.grupo] && (
          <ComposicaoPrecoItem item={itens.find((x) => x.id === detalhe.id) ?? detalhe} parametros={rodada.parametros[detalhe.grupo]!} />
        )}
      </Dialogo>

      <Dialogo aberto={dialogo === 'descartar'} titulo="Descartar rodada" aoFechar={() => setDialogo(null)}>
        {dialogo === 'descartar' && <Descartar id={id} aoConcluir={atualizar} aoFechar={() => setDialogo(null)} />}
      </Dialogo>
    </Pagina>
  );
}

function Resumo({ data }: { data: RodadaCompleta }) {
  const { resumo } = data;
  const cartoes = [
    { rotulo: 'Itens', valor: resumo.total },
    { rotulo: 'A atualizar', valor: resumo.atualizar, destaque: true },
    { rotulo: 'Preço mantido', valor: resumo.mantidos },
    { rotulo: 'Sem custo', valor: resumo.semCusto, alerta: resumo.semCusto > 0 },
    { rotulo: 'Sem peso', valor: resumo.semPeso, alerta: resumo.semPeso > 0 },
  ];
  return (
    <div className="mb-3 flex flex-wrap gap-3">
      {cartoes.map((c) => (
        <div key={c.rotulo} className="min-w-28 rounded-painel border border-linha bg-superficie px-3 py-2">
          <p className="text-[12px] text-tinta-2">{c.rotulo}</p>
          <p className={clsx('numero text-lg leading-6 font-semibold', c.destaque && 'text-acao-escura', c.alerta && 'text-aviso')}>{c.valor}</p>
        </div>
      ))}
      <div className="rounded-painel border border-linha bg-superficie px-3 py-2">
        <p className="text-[12px] text-tinta-2">Variação média ponderada (novo × atual)</p>
        <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
          {gruposPresentes(Object.keys(resumo.porGrupo)).map((g) => {
            const v = resumo.porGrupo[g.codigo]!.variacaoMediaPct;
            return (
              <p key={g.codigo} className="text-[13px]">
                {g.nome}: <strong className={clsx('numero', alertaVariacao(v) && 'text-aviso')}>{formatarPct(v, true)}</strong>
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Legenda() {
  return (
    <p className="ml-auto flex flex-wrap items-center gap-3 text-[12px] text-tinta-2">
      <span className="inline-flex items-center gap-1">
        <span className="inline-block size-3 rounded-sm border border-editado/50 bg-editado-fundo" /> editado
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block size-3 rounded-sm bg-aviso-fundo ring-1 ring-aviso-borda/60" /> variação &gt; +{LIMITES_ALERTA.variacaoMaxPct}% ou &lt;{' '}
        {LIMITES_ALERTA.variacaoMinPct}%
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block size-3 rounded-sm bg-erro-fundo ring-1 ring-erro/30" /> margem c/ desconto negativa
      </span>
    </p>
  );
}

const CABECALHO = [
  { t: 'Código', c: 'w-24' },
  { t: 'Descrição', c: 'min-w-56' },
  { t: 'Peso (kg)', c: 'text-right w-20' },
  { t: 'Custo/kg', c: 'text-right w-24' },
  { t: 'Acrésc./un', c: 'text-right w-20' },
  { t: 'Preço atual', c: 'text-right w-20' },
  { t: 'Margem líq. %', c: 'text-right w-24' },
  { t: 'Negoc. %', c: 'text-right w-24' },
  { t: 'Novo /kg', c: 'text-right w-20' },
  { t: 'Novo unit.', c: 'text-right w-28' },
  { t: 'Variação', c: 'text-right w-20' },
  { t: 'MC % cheia', c: 'text-right w-20' },
  { t: 'MC % c/ desc.', c: 'text-right w-20' },
  { t: 'MC R$/un', c: 'text-right w-24' },
  { t: 'Situação', c: 'w-28' },
];

function TabelaItens({
  itens,
  editavel,
  aoEditar,
  ocupado,
  comAcrescimo,
  travaAtiva,
  aoDetalhar,
}: {
  itens: ItemRodada[];
  comAcrescimo: boolean;
  travaAtiva: boolean;
  aoDetalhar: (item: ItemRodada) => void;
  editavel: boolean;
  aoEditar: (itemId: number, edicao: Edicao) => Promise<unknown>;
  ocupado?: number;
}) {
  const cabecalho = comAcrescimo ? CABECALHO : CABECALHO.filter((h) => h.t !== 'Acrésc./un');
  // Grupos antigos (rodadas de antes de 05/10/2026) aparecem no fim, para nenhum item sumir.
  const porGrupo = gruposPresentes(itens.map((i) => i.grupo)).map((g) => ({ ...g, itens: itens.filter((i) => i.grupo === g.codigo) }));
  return (
    <div className="overflow-x-auto rounded-painel border border-linha bg-superficie">
      <table className="w-full min-w-[1320px] border-collapse text-left text-[13px]">
        <thead className="sticky top-0 z-10 border-b border-linha bg-superficie text-[12px] leading-4 text-tinta-2">
          <tr>
            {cabecalho.map((h) => (
              <th key={h.t} className={clsx('px-2 py-2 font-medium', h.c)}>
                {h.t}
              </th>
            ))}
          </tr>
        </thead>
        {porGrupo.map((g) => (
          <tbody key={g.codigo}>
            <tr className="border-b border-linha bg-fundo">
              <th colSpan={cabecalho.length} className="px-2 py-1.5 text-left font-semibold">
                <span aria-hidden className="mr-2 inline-block h-3 w-[3px] rounded-sm bg-marca align-middle" />
                {g.nome} <span className="font-normal text-tinta-2">· {plural(g.itens.length, 'item', 'itens')}</span>
              </th>
            </tr>
            {g.itens.map((i) => (
              <Linha key={i.id} item={i} editavel={editavel} aoEditar={aoEditar} ocupado={ocupado === i.id} comAcrescimo={comAcrescimo} travaAtiva={travaAtiva} aoDetalhar={aoDetalhar} />
            ))}
          </tbody>
        ))}
        {porGrupo.length === 0 && (
          <tbody>
            <tr>
              <td colSpan={cabecalho.length} className="px-3 py-8 text-center text-tinta-2">
                Nenhum item com esses filtros.
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
}

function BotaoPadrao({ rotulo, aoClicar }: { rotulo: string; aoClicar: () => void }) {
  return (
    <button type="button" title={rotulo} aria-label={rotulo} onClick={aoClicar} className="shrink-0 cursor-pointer rounded p-0.5 text-editado hover:bg-editado-fundo">
      <RotateCcw aria-hidden className="size-3.5" />
    </button>
  );
}

const Linha = memo(
  function Linha({
    item: i,
    editavel,
    aoEditar,
    ocupado,
    comAcrescimo,
    travaAtiva,
    aoDetalhar,
  }: {
    item: ItemRodada;
    editavel: boolean;
    aoEditar: (itemId: number, edicao: Edicao) => Promise<unknown>;
    ocupado: boolean;
    comAcrescimo: boolean;
    travaAtiva: boolean;
    aoDetalhar: (item: ItemRodada) => void;
  }) {
    const mantido = i.situacao === 'mantido';
    // Cadeado só onde faz diferença: item mantido (travado) ou item com o preço liberado.
    const mostrarCadeado = travaAtiva && (mantido || i.liberarPreco);
    const dicaCadeado = mantido
      ? `Preço mantido: o calculado seria ${formatarReais(i.precoCalculado)}. Clique para liberar o preço calculado neste item.`
      : 'Preço liberado neste item (segue o cálculo). Clique para manter o preço atual de novo.';
    const variacao = alertaVariacao(i.variacaoPct);
    const margemNeg = alertaMargem(i.margemDesconto);
    const precosDiferentes = i.qtdLinhas > 1 && i.precoAtual !== i.precoAtualMin;
    const erro = i.situacao === 'erro';

    return (
      <tr className={clsx('border-b border-linha-suave last:border-0', margemNeg || erro ? 'bg-erro-fundo/60' : variacao && 'bg-aviso-fundo/60', ocupado && 'opacity-60')}>
        <td className="px-2 py-1 font-mono whitespace-nowrap">{i.cdMaterial}</td>
        <td className="px-2 py-1">
          {i.descricao}
          {i.qtdLinhas > 1 && (
            <span className={clsx('ml-1.5 text-[11px] whitespace-nowrap', precosDiferentes ? 'font-semibold text-aviso' : 'text-tinta-2')} title={precosDiferentes ? `Preços atuais entre ${formatarReais(i.precoAtualMin)} e ${formatarReais(i.precoAtual)}` : undefined}>
              {i.qtdLinhas} linhas{precosDiferentes && ' · preços diferentes'}
            </span>
          )}
        </td>
        <td className="numero px-2 py-1 text-right">{formatarPeso(i.peso)}</td>
        <td className="numero px-2 py-1 text-right">{formatarCusto(i.custoKg)}</td>
        {comAcrescimo && (
          <td className={clsx('numero px-2 py-1 text-right', i.acrescimoUnit > 0 ? 'font-medium' : 'text-tinta-2')} title="Somado ao preço unitário depois do cálculo">
            {i.acrescimoUnit > 0 ? `+${formatarReais(i.acrescimoUnit)}` : '—'}
          </td>
        )}
        <td className="numero px-2 py-1 text-right">
          {formatarReais(i.precoAtual)}
          {precosDiferentes && <span className="block text-[11px] text-aviso">mín. {formatarReais(i.precoAtualMin)}</span>}
        </td>
        <td className="px-2 py-1">
          {editavel ? (
            <div className="flex items-center gap-0.5">
              <EntradaNumero rotulo={`Margem líquida de ${i.cdMaterial}`} valor={i.margemPct} casas={4} sufixo="%" destaque={i.margemEditada} aoConfirmar={(v) => aoEditar(i.id, { margemPct: v })} />
              {i.margemEditada && <BotaoPadrao rotulo="Voltar à margem do grupo" aoClicar={() => void aoEditar(i.id, { margemPct: null })} />}
            </div>
          ) : (
            <span className={clsx('numero block text-right', i.margemEditada && 'font-medium text-editado')}>{formatarPct(i.margemPct)}</span>
          )}
        </td>
        <td className="px-2 py-1">
          {editavel ? (
            <div className="flex items-center gap-0.5">
              <EntradaNumero rotulo={`Negociação de ${i.cdMaterial}`} valor={i.negociacaoPct} casas={4} sufixo="%" destaque={i.negociacaoEditada} aoConfirmar={(v) => aoEditar(i.id, { negociacaoPct: v })} />
              {i.negociacaoEditada && <BotaoPadrao rotulo="Voltar à negociação do grupo" aoClicar={() => void aoEditar(i.id, { negociacaoPct: null })} />}
            </div>
          ) : (
            <span className={clsx('numero block text-right', i.negociacaoEditada && 'font-medium text-editado')}>{formatarPct(i.negociacaoPct)}</span>
          )}
        </td>
        <td className="numero px-2 py-1 text-right text-tinta-2">{formatarReaisKg(i.precoKg)}</td>
        <td className="px-2 py-1">
          {editavel ? (
            <div className="flex items-center gap-0.5">
              <EntradaNumero rotulo={`Preço novo unitário de ${i.cdMaterial}`} valor={i.precoUnit} casas={2} casasFixas destaque={i.precoEditado} aoConfirmar={(v) => aoEditar(i.id, { precoUnit: v })} className="font-semibold" />
              {i.precoEditado && <BotaoPadrao rotulo="Voltar ao preço calculado" aoClicar={() => void aoEditar(i.id, { precoUnit: null })} />}
              {mostrarCadeado && (
                <button
                  type="button"
                  title={dicaCadeado}
                  aria-label={dicaCadeado}
                  onClick={() => void aoEditar(i.id, { liberarPreco: mantido })}
                  className={clsx('shrink-0 cursor-pointer rounded p-0.5', mantido ? 'text-aviso hover:bg-aviso-fundo' : 'text-tinta-2 hover:bg-fundo')}
                >
                  {mantido ? <Lock aria-hidden className="size-3.5" /> : <LockOpen aria-hidden className="size-3.5" />}
                </button>
              )}
            </div>
          ) : (
            <span className={clsx('numero flex items-center justify-end gap-1 font-semibold', i.precoEditado && 'text-editado')} title={mantido ? dicaCadeado.split('.')[0] : undefined}>
              {formatarReais(i.precoUnit)}
              {mantido && <Lock aria-hidden className="size-3.5 text-aviso" />}
            </span>
          )}
        </td>
        <td className={clsx('numero px-2 py-1 text-right', variacao && 'font-semibold text-aviso')}>{formatarPct(i.variacaoPct, true)}</td>
        <td className="numero px-2 py-1 text-right">{formatarPct(i.margemCheia)}</td>
        <td className={clsx('numero px-2 py-1 text-right', margemNeg && 'font-semibold text-erro')}>{formatarPct(i.margemDesconto)}</td>
        <td className="px-2 py-1 text-right">
          {i.mcUnit !== null ? (
            <button
              type="button"
              onClick={() => aoDetalhar(i)}
              title="Ver a composição do preço e a margem de contribuição"
              className={clsx('numero cursor-pointer rounded px-1 underline decoration-dotted underline-offset-2 hover:bg-acao-clara', i.mcUnit < 0 ? 'font-semibold text-erro' : 'text-acao-escura')}
            >
              {formatarReaisKg(i.mcUnit)}
            </button>
          ) : (
            <span className="text-tinta-2">—</span>
          )}
        </td>
        <td className="px-2 py-1 text-[12px]">
          {erro ? (
            <span className="text-erro">{i.motivo}</span>
          ) : mantido ? (
            <span className="font-medium text-aviso" title={`Calculado: ${formatarReais(i.precoCalculado)}`}>
              Preço mantido
            </span>
          ) : i.precoEditado ? (
            <span className="font-medium text-editado">Preço manual</span>
          ) : (
            <span className="text-tinta-2">Atualizar</span>
          )}
        </td>
      </tr>
    );
  },
  (a, b) => a.editavel === b.editavel && a.ocupado === b.ocupado && a.comAcrescimo === b.comAcrescimo && a.travaAtiva === b.travaAtiva && a.aoEditar === b.aoEditar && a.aoDetalhar === b.aoDetalhar && JSON.stringify(a.item) === JSON.stringify(b.item),
);

function NaoAlterados({ itens }: { itens: ItemRodada[] }) {
  return (
    <section className="mt-6">
      <h2 className="mb-1 font-semibold">Itens que não serão alterados</h2>
      <p className="mb-2 text-[13px] text-tinta-2">Mantêm o preço atual do CIGAM. Cadastre o custo (tela Custos) ou corrija o peso no CIGAM e use "Recarregar custos".</p>
      <div className="overflow-x-auto rounded-painel border border-linha bg-superficie">
        <table className="w-full min-w-[720px] text-left text-[13px]">
          <thead className="border-b border-linha text-[12px] text-tinta-2">
            <tr>
              <th className="px-3 py-2 font-medium">Código</th>
              <th className="px-3 py-2 font-medium">Descrição</th>
              <th className="px-3 py-2 font-medium">Grupo</th>
              <th className="px-3 py-2 text-right font-medium">Preço atual</th>
              <th className="px-3 py-2 font-medium">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i) => (
              <tr key={i.id} className="border-b border-linha-suave last:border-0">
                <td className="px-3 py-1.5 font-mono">{i.cdMaterial}</td>
                <td className="px-3 py-1.5">{i.descricao}</td>
                <td className="px-3 py-1.5 text-tinta-2">{nomeGrupo(i.grupo)}</td>
                <td className="numero px-3 py-1.5 text-right">{formatarReais(i.precoAtual)}</td>
                <td className="px-3 py-1.5 text-aviso">{i.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EditarParametros({
  data,
  grupos,
  aoConcluir,
}: {
  data: RodadaCompleta;
  grupos: CodigoGrupo[];
  aoConcluir: (r: RodadaCompleta & { preservados: number }) => void;
}) {
  const inicial = useMemo(() => paraTextos(grupos, data.rodada.parametros), [grupos, data.rodada.parametros]);
  const [textos, setTextos] = useState<TextosParametros>(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarErros, setMostrarErros] = useState(false);
  const salvar = useMutation({
    mutationFn: (p: Parameters<typeof apiRodadas.atualizarParametros>[1]) => apiRodadas.atualizarParametros(data.rodada.id, p),
    onSuccess: aoConcluir,
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível recalcular.'),
  });
  const editados = data.itens.filter(editado).length;

  function enviar() {
    setMostrarErros(true);
    setErro(null);
    // Só os grupos que mudaram são recalculados.
    const mudaram = Object.fromEntries(grupos.filter((g) => JSON.stringify(textos[g]) !== JSON.stringify(inicial[g])).map((g) => [g, textos[g]]));
    if (Object.keys(mudaram).length === 0) {
      setErro('Nenhum parâmetro foi alterado.');
      return;
    }
    const { parametros, erros } = lerTextos(mudaram);
    const primeiro = Object.entries(erros)[0];
    if (primeiro) {
      setErro(`${nomeGrupo(primeiro[0])}: ${primeiro[1]}`);
      return;
    }
    salvar.mutate(parametros);
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tinta-2">Os grupos alterados são recalculados inteiros. Margem, negociação e preço editados manualmente em cada item são preservados.</p>
      {editados > 0 && <Aviso tipo="aviso">{plural(editados, 'item tem edição manual e será preservado', 'itens têm edição manual e serão preservados')}.</Aviso>}
      <FormParametros grupos={grupos} textos={textos} aoMudar={setTextos} mostrarErros={mostrarErros} />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <div className="flex justify-end border-t border-linha-suave pt-4">
        <Botao variante="principal" carregando={salvar.isPending} onClick={enviar}>
          Recalcular
        </Botao>
      </div>
    </div>
  );
}

function Publicar({ data, aoConcluir, aoFechar }: { data: RodadaCompleta; aoConcluir: (r: RodadaCompleta) => void; aoFechar: () => void }) {
  const { data: sessao } = useSessao();
  const navegar = useNavigate();
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [divergencias, setDivergencias] = useState<Divergencia[] | null>(null);
  const [resultado, setResultado] = useState<ResultadoPublicacao | null>(null);
  const { rodada, resumo } = data;
  const naoAlterados = resumo.total - resumo.atualizar - resumo.mantidos;
  const confere = confirmacao.trim().toUpperCase() === rodada.tbPreco.toUpperCase();

  const publicar = useMutation({
    mutationFn: () => apiRodadas.publicar(rodada.id, confirmacao),
    onSuccess: (r) => {
      aoConcluir(r);
      setResultado(r.resultado);
    },
    onError: (e) => {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível publicar.');
      if (e instanceof ErroApi && e.codigo === 'PRECOS_ALTERADOS_NO_CIGAM') {
        setDivergencias((e.detalhes as { divergencias: Divergencia[] }).divergencias);
      }
    },
  });

  if (resultado) {
    return (
      <div className="flex flex-col gap-4">
        {resultado.efetivado ? (
          <Aviso tipo="ok" titulo="Preços publicados no CIGAM">
            {plural(resultado.materiais, 'material atualizado', 'materiais atualizados')} ({plural(resultado.linhasAtualizadas, 'linha', 'linhas')} da GEPRECTA)
            {resultado.ambiente && <> em {resultado.ambiente}</>}. O app dos representantes recebe os preços novos pela sincronização do Vitalbyte.
          </Aviso>
        ) : (
          <Aviso tipo="aviso" titulo="Simulação concluída — nada foi gravado">
            A gravação rodou até o fim e foi desfeita: {plural(resultado.materiais, 'material seria atualizado', 'materiais seriam atualizados')} (
            {plural(resultado.linhasAtualizadas, 'linha', 'linhas')}). Para gravar de verdade, o administrador precisa ligar CIGAM_WRITE_MODE=live.
          </Aviso>
        )}
        <div className="flex justify-end gap-2 border-t border-linha-suave pt-4">
          {resultado.efetivado && (
            <Botao variante="secundario" onClick={() => navegar(`/pdf?tabela=${encodeURIComponent(rodada.tbPreco)}`)}>
              <FileDown aria-hidden className="size-4" /> Gerar PDF
            </Botao>
          )}
          <Botao variante="principal" onClick={aoFechar}>
            Fechar
          </Botao>
        </div>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (confere) {
          setErro(null);
          setDivergencias(null);
          publicar.mutate();
        }
      }}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        <dt className="text-tinta-2">Tabela</dt>
        <dd className="font-medium">
          {rodada.tbPreco} — {rodada.descricaoTabela}
        </dd>
        <dt className="text-tinta-2">Banco</dt>
        <dd>{sessao && <SeloAmbiente sessao={sessao} />}</dd>
        <dt className="text-tinta-2">A atualizar</dt>
        <dd className="font-semibold">{plural(resumo.atualizar, 'item', 'itens')}</dd>
        {resumo.mantidos > 0 && (
          <>
            <dt className="text-tinta-2">Preço mantido</dt>
            <dd>{plural(resumo.mantidos, 'item', 'itens')} (manter preços atuais: não são gravados)</dd>
          </>
        )}
        <dt className="text-tinta-2">Não alterados</dt>
        <dd>{plural(naoAlterados, 'item', 'itens')} (mantêm o preço atual)</dd>
      </dl>
      {sessao?.ambiente.modoEscrita === 'dryrun' ? (
        <Aviso tipo="info">Modo simulação: a gravação roda no CIGAM e é desfeita no final. Serve para conferir antes de gravar de verdade.</Aviso>
      ) : (
        <Aviso tipo="aviso">
          Os preços da GEPRECTA serão <strong>sobrescritos</strong> (preço unitário, maior e menor; desconto zerado). Não há desfazer automático.
        </Aviso>
      )}
      {resumo.erro > 0 && <Aviso tipo="erro">{plural(resumo.erro, 'item está com erro de cálculo e não será publicado', 'itens estão com erro de cálculo e não serão publicados')}.</Aviso>}
      <Campo
        rotulo={`Digite o código da tabela (${rodada.tbPreco}) para confirmar`}
        value={confirmacao}
        onChange={(e) => setConfirmacao(e.target.value)}
        autoFocus
        autoComplete="off"
        spellCheck={false}
      />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {divergencias && (
        <div className="max-h-48 overflow-y-auto rounded-painel border border-linha text-[13px]">
          <table className="w-full text-left">
            <thead className="border-b border-linha text-[12px] text-tinta-2">
              <tr>
                <th className="px-2 py-1 font-medium">Material</th>
                <th className="px-2 py-1 text-right font-medium">Na prévia</th>
                <th className="px-2 py-1 text-right font-medium">No CIGAM agora</th>
              </tr>
            </thead>
            <tbody>
              {divergencias.map((d) => (
                <tr key={d.cdMaterial} className="border-b border-linha-suave last:border-0">
                  <td className="px-2 py-1">
                    <span className="font-mono">{d.cdMaterial}</span> {d.descricao}
                  </td>
                  <td className="numero px-2 py-1 text-right">
                    {formatarReais(d.esperado.preco)} ({d.esperado.linhas}×)
                  </td>
                  <td className="numero px-2 py-1 text-right">{d.atual ? `${formatarReais(d.atual.preco)} (${d.atual.linhas}×)` : 'saiu da tabela'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t border-linha-suave px-2 py-1.5 text-tinta-2">
            Descarte esta rodada e <Link to={`/rodadas/nova?tabela=${encodeURIComponent(rodada.tbPreco)}`} className="text-acao underline">crie uma nova</Link> para recalcular com os preços atuais.
          </p>
        </div>
      )}
      <div className="flex justify-end gap-2 border-t border-linha-suave pt-4">
        <Botao variante="discreto" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao type="submit" variante="principal" disabled={!confere} carregando={publicar.isPending}>
          <Upload aria-hidden className="size-4" /> {sessao?.ambiente.modoEscrita === 'dryrun' ? 'Simular publicação' : `Publicar ${plural(resumo.atualizar, 'preço', 'preços')}`}
        </Botao>
      </div>
    </form>
  );
}

function Descartar({ id, aoConcluir, aoFechar }: { id: number; aoConcluir: (r: RodadaCompleta) => void; aoFechar: () => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const descartar = useMutation({
    mutationFn: () => apiRodadas.descartar(id),
    onSuccess: (r) => {
      aoConcluir(r);
      aoFechar();
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível descartar.'),
  });
  return (
    <div className="flex flex-col gap-4">
      <p>A rodada deixa de poder ser editada ou publicada. Ela continua no histórico. Nada é alterado no CIGAM.</p>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <div className="flex justify-end gap-2 border-t border-linha-suave pt-4">
        <Botao variante="discreto" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao variante="perigo" carregando={descartar.isPending} onClick={() => descartar.mutate()}>
          Descartar rodada
        </Botao>
      </div>
    </div>
  );
}
